import { compileDashboardFeature, DashboardComponentType } from '@/features/dashboard'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import { EntityLevel } from '@/composables/useEntityLevel'
import { formatPercent, formatArea } from '@/features/analytics/formats'

const enabledEntityLevels: EntityLevel[] = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION']

const formatWault: FormatOptions = {
  format: { type: 'year-month' }
}

const formatMillions: FormatOptions = {
  format: { type: 'multiply', value: 0.000001 },
  steps: [
    { format: { type: 'intl-number', maximumFractionDigits: 1 } },
    { format: { type: 'format-string', format: '%smn' } }
  ]
}

const formatAreaThousands: FormatOptions = {
  format: { type: 'multiply', value: 0.001 },
  steps: [
    { format: { type: 'intl-number', maximumFractionDigits: 1 } },
    { format: { type: 'format-string', format: '%st' } }
  ]
}

export const rentalObjectsAnalysis = compileDashboardFeature({
  name: 'rental-object-analysis',
  title: {
    sv: 'Hyresobjekts&shy;analys',
    en: 'Rental Objects Analysis'
  },
  dataSectionTitle: {
    sv: 'Hyresobjektsanalys',
    en: 'Rental Objects Analysis'
  },
  icon: 'IconHomeWork',
  enabled: false,
  defaultToggle: 'all',
  enabledEntityLevels,
  defaultFilters: {
    calendar: {},
    defaultDateSelection: 'last24h',
    dateSelections: [
      'last24h',
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ],
    filterTags: ['rental_object_type', 'property_type'],
    entityLevels: ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION']
  },
  measurements: [
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'leasable_area',
        expression: { type: 'sum', exp: { type: 'field', name: 'leasable_area' } }
      },
      title: { sv: 'Uthyrbar yta', en: 'Leasable Area' },
      options: {
        color: '#40A9FF',
        enabledEntityLevels,
        format: formatAreaThousands,
        min: 0,
        max: 10000,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'vacancy_rate',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacancy_rate' } }
      },
      title: { sv: 'Vakansgrad % (m²)', en: 'Vacancy Rate % (m²)' },
      options: {
        color: '#FFC53D',
        enabledEntityLevels,
        format: formatPercent,
        min: 0,
        max: 1,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'wault',
        expression: { type: 'sum', exp: { type: 'field', name: 'wault' } }
      },
      title: {
        sv: 'Weighted Average Unexpired Lease Term',
        en: 'Weighted Average Unexpired Lease Term'
      },
      options: {
        color: '#F759AB',
        enabledEntityLevels,
        format: formatWault,
        min: 0,
        max: 10,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'gross_rent',
        expression: { type: 'sum', exp: { type: 'field', name: 'gross_rent' } }
      },
      title: { sv: 'Bruttohyra', en: 'Gross Rent' },
      options: {
        color: '#36CFC9',
        enabledEntityLevels,
        format: formatMillions,
        min: 0,
        max: 1000000,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'net_rent',
        expression: { type: 'sum', exp: { type: 'field', name: 'net_rent' } }
      },
      title: { sv: 'Nettohyra', en: 'Net Rent' },
      options: {
        color: '#52C41A',
        enabledEntityLevels,
        format: formatMillions,
        min: 0,
        max: 1000000,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'economic_vacancy',
        expression: { type: 'sum', exp: { type: 'field', name: 'economic_vacancy' } }
      },
      title: { sv: 'Ekonomisk vakans (€)', en: 'Economic Vacancy (€)' },
      options: {
        color: '#FFA940',
        enabledEntityLevels,
        format: formatAreaThousands,
        min: 0,
        max: 1000000,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects_kpi',
      field: {
        name: 'vacancy_rate_hist',
        expression: {
          type: 'histogram',
          min: 0,
          max: 1,
          nbuckets: 10,
          exp: { type: 'field', name: 'vacancy_rate' }
        }
      },
      title: { sv: 'Vakansgrad', en: 'Vacancy Rate' },
      options: {
        colorScale: { type: 'SINGLE', cutoffs: [{ color: '#FFC53D' }] },
        enabledEntityLevels,
        format: formatPercent
      }
    },
    {
      name: 'rental_objects',
      field: {
        name: 'vacant_area',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacant_area' } }
      },
      title: { sv: 'Vakant yta', en: 'Vacant Area' },
      options: {
        color: '#73D13D',
        enabledEntityLevels,
        format: formatArea,
        min: 0,
        max: 10000,
        legend: { type: 'slider' }
      }
    },
    {
      name: 'rental_objects',
      field: {
        name: 'vacant_area_apartment',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacant_area' } }
      },
      title: { sv: 'Lägenhet', en: 'Apartment' },
      options: { color: '#40A9FF', enabledEntityLevels, format: formatArea, min: 0, max: 10000 },
      filters: {
        values: {
          query: {
            query: {
              filters: { type: 'in' as const, key: 'rental_object_type', values: ['apartment'] }
            }
          }
        }
      }
    },
    {
      name: 'rental_objects',
      field: {
        name: 'vacant_area_base_station',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacant_area' } }
      },
      title: { sv: 'Basstation', en: 'Base Station' },
      options: { color: '#9254DE', enabledEntityLevels, format: formatArea, min: 0, max: 10000 },
      filters: {
        values: {
          query: {
            query: {
              filters: { type: 'in' as const, key: 'rental_object_type', values: ['base_station'] }
            }
          }
        }
      }
    },
    {
      name: 'rental_objects',
      field: {
        name: 'vacant_area_business_premises',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacant_area' } }
      },
      title: { sv: 'Verksamhetslokaler', en: 'Business Premises' },
      options: { color: '#F759AB', enabledEntityLevels, format: formatArea, min: 0, max: 10000 },
      filters: {
        values: {
          query: {
            query: {
              filters: {
                type: 'in' as const,
                key: 'rental_object_type',
                values: ['business_premises']
              }
            }
          }
        }
      }
    },
    {
      name: 'rental_objects',
      field: {
        name: 'vacant_area_car_parking',
        expression: { type: 'sum', exp: { type: 'field', name: 'vacant_area' } }
      },
      title: { sv: 'Bilparkering', en: 'Car Parking' },
      options: { color: '#FFA940', enabledEntityLevels, format: formatArea, min: 0, max: 10000 },
      filters: {
        values: {
          query: {
            query: {
              filters: { type: 'in' as const, key: 'rental_object_type', values: ['car_parking'] }
            }
          }
        }
      }
    }
  ],
  components: [
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'leasable-area-kpi',
      measurements: ['rental_objects_kpi.leasable_area'],
      info: {
        sv: 'Summering av arean för varje enskilt hyresobjekt i beståndet. Exklusive gemensamma utrymmen och tekniska ytor. Visas i tusental (t), enhet m².',
        en: 'Sum of the area for each individual rental object in the portfolio. Excluding common areas and technical spaces. Displayed in thousands (t), unit m².'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'vacancy-rate-kpi',
      measurements: ['rental_objects_kpi.vacancy_rate'],
      info: {
        sv: 'Andel av total uthyrbar area som är vakant vid mättillfället: vakant area (m²) ÷ total uthyrbar area (m²), redovisad i procent.',
        en: 'Share of total leasable area that is vacant at the measurement point: vacant area (m²) ÷ total leasable area (m²), reported as a percentage.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'wault-kpi',
      measurements: ['rental_objects_kpi.wault'],
      info: {
        sv: 'Vägd genomsnittlig återstående löptid (WAULT): Σ(återstående år × bruttohyra per år) ÷ Σ(bruttohyra per år). Exkluderar vakanskontrat och utgångna kontrakt.',
        en: 'Weighted Average Unexpired Lease Term (WAULT): Σ(remaining years × gross rent yearly) ÷ Σ(gross rent yearly). Excludes vacancy contracts and expired contracts.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'gross-rent-kpi',
      measurements: ['rental_objects_kpi.gross_rent'],
      info: {
        sv: 'Summa kontraktsrader av typ Rent och Supplements, gällande per rapportdatum. Omräknat till euro (€) via ECB-kurs och skalat till valt tidsintervall (år/månad). Visas i miljoner (mn).',
        en: 'Sum of contract lines of type Rent and Supplements, valid at the reporting date. Converted to EUR (€) via ECB exchange rate and scaled to the selected time period (year/month). Displayed in millions (mn).'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'net-rent-kpi',
      measurements: ['rental_objects_kpi.net_rent'],
      info: {
        sv: 'Summa kontraktsrader av typ Rent, gällande per rapportdatum. Omräknat till euro (€) via ECB-kurs och skalat till valt tidsintervall (år/månad). Visas i miljoner (mn).',
        en: 'Sum of contract lines of type Rent, valid at the reporting date. Converted to EUR (€) via ECB exchange rate and scaled to the selected time period (year/month). Displayed in millions (mn).'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      disableMap: true,
      name: 'economic-vacancy-kpi',
      measurements: ['rental_objects_kpi.economic_vacancy'],
      info: {
        sv: 'Summa av redovisningshyran för vakanta hyresobjekt inom aktuellt datumintervall. Simulerar hyresvärde om beståndet vore helt uthyrt, i euro.',
        en: 'Sum of the accounting/registered rent (redovisningshyra) for vacant rental objects within the current date range. Simulates rental value if the portfolio were fully leased, in euros.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'gross-rent-timeseries',
      measurements: ['rental_objects_kpi.gross_rent'],
      title: { sv: 'Bruttohyra över tid', en: 'Gross Rent Over Time' },
      info: {
        sv: 'Visar hur bruttohyran förändras över den valda perioden.',
        en: 'Shows how the gross rent changes over the selected period.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      enabled: false,
      name: 'vacant-area-timeseries',
      measurements: ['rental_objects.vacant_area'],
      title: { sv: 'Vakant yta över tid', en: 'Vacant Area Over Time' },
      info: {
        sv: 'Visar hur den absoluta vakanta ytan (m²) förändras över den valda perioden. Skiljer sig från vakansgraden som är ett procentmått.',
        en: 'Shows how the absolute vacant area (m²) changes over the selected period. Differs from vacancy rate which is a percentage measure.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      enabled: false,
      name: 'vacant-area-by-unit-type',
      measurements: [
        'rental_objects.vacant_area_apartment',
        'rental_objects.vacant_area_base_station',
        'rental_objects.vacant_area_business_premises',
        'rental_objects.vacant_area_car_parking'
      ],
      title: { sv: 'Vakant yta per nyttjandetyp (enhet)', en: 'Vacant Area by Type of Use (Unit)' },
      info: {
        sv: 'Visar vakant yta (m²) över tid per nyttjandetyp. Använd kryssrutorna för att visa/dölja enskilda enhetstyper.',
        en: 'Shows vacant area (m²) over time per type of use. Use the checkboxes to show/hide individual unit types.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'rental-objects-toplist-vacancy',
      grouping: 'stacked',
      measurements: ['rental_objects_kpi.vacancy_rate'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'vacancy_rate', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: { sv: 'Vakansgrad per hyresobjekt', en: 'Vacancy Rate per Rental Object' },
      info: {
        sv: 'Topplista över vakansgrad per hyresobjekt, sorterat från högsta till lägsta vakans.',
        en: 'Top list of vacancy rate per rental object, sorted from highest to lowest vacancy.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'rental-objects-toplist-gross-rent',
      grouping: 'stacked',
      measurements: ['rental_objects_kpi.gross_rent'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'gross_rent', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: { sv: 'Bruttohyra per hyresobjekt', en: 'Gross Rent per Rental Object' },
      info: {
        sv: 'Topplista över bruttohyra per hyresobjekt, sorterat från högsta till lägsta hyra.',
        en: 'Top list of gross rent per rental object, sorted from highest to lowest rent.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'rental-objects-toplist-wault',
      grouping: 'stacked',
      measurements: ['rental_objects_kpi.wault'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'wault', direction: 'ASC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'WAULT per hyresobjekt (kortast löptid)',
        en: 'WAULT per Rental Object (Shortest First)'
      },
      info: {
        sv: 'Visar återstående viktad genomsnittlig löptid per hyresobjekt, sorterat stigande. Objekt överst har kortast kvarvarande kontraktstid och utgör störst förnyelseriisk.',
        en: 'Shows the weighted average unexpired lease term per rental object, sorted ascending. Objects at the top have the shortest remaining lease term and represent the highest renewal risk.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'rental-objects-toplist-economic-vacancy',
      grouping: 'stacked',
      measurements: ['rental_objects_kpi.economic_vacancy'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'economic_vacancy', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: { sv: 'Ekonomisk vakans per hyresobjekt', en: 'Economic Vacancy per Rental Object' },
      info: {
        sv: 'Topplista över ekonomisk vakans per hyresobjekt, sorterat från högsta till lägsta ekonomisk förlust. Visar var i beståndet vakanskostnaden är störst.',
        en: 'Top list of economic vacancy per rental object, sorted from highest to lowest economic loss. Shows where in the portfolio the vacancy cost is greatest.'
      }
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'vacancy-rate-histogram',
      measurements: ['rental_objects_kpi.vacancy_rate_hist'],
      normalize: false,
      showRanges: true,
      title: { sv: 'Fördelning av vakansgrad', en: 'Vacancy Rate Distribution' },
      info: {
        sv: 'Visar hur vakansgraden fördelar sig över beståndet, uppdelat i 10 intervall från 0 % till 100 %. Hjälper till att identifiera om vakanser är koncentrerade eller spridda.',
        en: 'Shows how vacancy rate is distributed across the portfolio, split into 10 intervals from 0% to 100%. Helps identify whether vacancies are concentrated or spread out.'
      }
    }
  ]
})
